import { ServiceProduct } from '@app/services/ServiceProduct';
import { ECommerceProductInfo } from '../ServiceProduct/ServiceProduct';
import { CategoryEnum } from '@app/enums/CategoryEnum';
import { DiskTypeEnum, DiskTypeEnumDescription } from '@app/enums/DiskTypeEnum';

export interface ECommerceProduct {
  id: number;
  part: number;
  quantity?: number;
  position?: number;
}

export interface UserInfo {
  lastName: string;
  firstName: string;
  middleName: string;
  email: string;
  mobilePhone: string;
}

class ServiceEcommerce {
  inCart = [];

  getStackTrace() {
    let stack: any;

    try {
      throw new Error('');
    } catch (error) {
      stack = error.stack || '';
    }

    stack = stack.split('\n');
    for (let i = 0; i < stack.length; i++) {
      stack[i] = stack[i].replace(/^\s*/gi, '').replace(/\s*$/gi, '');
    }

    return stack.splice(stack[0] === 'Error' ? 2 : 1);
  }

  isNil(value) {
    return typeof value === 'undefined' || value === null;
  }

  //  Обычно свойства продуктов которые присылают в функции этого модуля -
  //  это строки или цифры.
  //  Мы хотим посылать в ECommerce либо null либо строку.
  //  Было бы удобно иметь какую-то стандартную функцию в которое посылаешь
  //  свойство товара - может быть чушь или неправильный формат или тип - а
  //  на выходе она генерирует строку -
  //  если на входе была цифра или строка - на выходе строка;
  //  если на входе была undefined или null - на выходе null;
  //  если на входе была true - на выходе 'true';
  //  если на входе была false - на выходе 'false';
  //  если на входе был какой-то другой тип - на выходе 'unexpected type {type}'.
  convertAtomToStringOrNull(value) {
    const valueType = typeof value;

    if (valueType === 'number') {
      return value.toString();
    }

    if (valueType === 'string') {
      return value;
    }

    if (valueType === 'undefined' || value === null) {
      return null;
    }

    if (value === true) {
      return 'true';
    }

    if (value === false) {
      return 'false';
    }

    return 'unexpected type ' + valueType;
  }

  info(items: ECommerceProduct[], callbackFn: (param: any) => any) {
    if (!Array.isArray(items)) {
      callbackFn([]);
      return;
    }

    if (items.length === 0) {
      callbackFn([]);
      return;
    }

    const tuples = [];
    items.forEach(item => {
      const id = item.id;
      const part = item.part;
      tuples.push(id + '-' + part);
    });

    ServiceProduct.getProductsFromTuples(tuples).then(list => {
      callbackFn(list);
    });
  }

  homePage() {
    window.dataLayer.push({
      event: 'HomePage',
    });
  }

  promotionClick(name, redirect) {
    window.dataLayer.push({
      event: 'promotionClick',
      ecommerce: {
        promoClick: {
          promotions: [
            {
              name,
            },
          ],
        },
      },
      eventCallback: function () {
        document.location = redirect;
      },
    });
  }

  promoView(promotions: ECommerceProduct[]) {
    window.dataLayer.push({
      ecommerce: {
        promoView: {
          promotions,
        },
      },
    });
  }

  productInfo(item: ECommerceProductInfo) {
    const categoryType = this.convertAtomToStringOrNull(item.category);
    const category = [];

    category.push(categoryType);
    if (item.product.brand.fullName) {
      category.push(this.convertAtomToStringOrNull(item.product.brand.fullName));
    }

    if (categoryType === CategoryEnum.Tyres) {
      category.push(this.convertAtomToStringOrNull(item.product.model.fullName));
      category.push(
        this.convertAtomToStringOrNull(item.product.width) +
          ' ' +
          this.convertAtomToStringOrNull(item.product.height) +
          ' R' +
          this.convertAtomToStringOrNull(item.product.radius),
      );
      category.push(item.product.season > 0 ? 'Зима' : 'Лето');
    } else if (categoryType === CategoryEnum.Disks) {
      category.push(this.convertAtomToStringOrNull(item.product.model.fullName));
      category.push(
        this.convertAtomToStringOrNull(item.product.width) +
          'х' +
          this.convertAtomToStringOrNull(item.product.radius) +
          ' ' +
          this.convertAtomToStringOrNull(`${item.product.pcde}x{${item.product.pcdd}`) +
          ' ET' +
          this.convertAtomToStringOrNull(item.product.et) +
          ' Dia' +
          this.convertAtomToStringOrNull(item.product.dia),
      );

      switch (item.product.model.type) {
        case DiskTypeEnum.Litie:
          category.push(DiskTypeEnumDescription[DiskTypeEnum.Litie]);
          break;
        case DiskTypeEnum.Kovan:
          category.push(DiskTypeEnumDescription[DiskTypeEnum.Kovan]);
          break;
        case DiskTypeEnum.Gruz:
          category.push(DiskTypeEnumDescription[DiskTypeEnum.Gruz]);
          break;
        default:
          category.push(DiskTypeEnumDescription[DiskTypeEnum.Shtamp]);
      }
    } else if (categoryType === CategoryEnum.Battery) {
      category.push('null');
      category.push(this.convertAtomToStringOrNull(item.product.name));
      category.push('null');
    }

    return {
      part: item.part,
      name: 'p_' + this.convertAtomToStringOrNull(item.product.name),
      id: this.convertAtomToStringOrNull(item.product.rn),
      brand: this.convertAtomToStringOrNull(item.product.brand.fullName),
      category: category.join('/'),
      variant: this.convertAtomToStringOrNull(item.product.name),
      dimension17: this.convertAtomToStringOrNull(item.product.model.id),
      dimension18: this.convertAtomToStringOrNull(item.product.mnfCode),
      price: this.convertAtomToStringOrNull(item.product.price),
      quantity: 0,
      list: null,
      position: null,
    };
  }

  detail(items: ECommerceProduct[], event = null) {
    try {
      this.info(items, data => {
        const products = [];

        data?.forEach(item => {
          products.push(this.productInfo(item));
        });

        window.dataLayer.push({
          event: 'productDetail',
          eventLabel: event === 'Quick view' ? 'Quick view' : 'Product page',
          ecommerce: {
            detail: {
              products,
            },
          },
        });
      });
    } catch (e) {
      console.error('[ECommerce] Error: ' + e.message);
    }
  }

  fastOrderOpen(item: ECommerceProduct, label) {
    try {
      this.info([item], data => {
        const products = [];

        data?.forEach(value => {
          const product = this.productInfo(value);
          product.quantity = this.convertAtomToStringOrNull(item.quantity || 1);
          products.push(product);
        });

        window.dataLayer.push({
          event: 'fastOrderOpen',
          eventLabel: label,
          ecommerce: {
            add: {
              products,
            },
          },
        });
      });
    } catch (e) {
      console.error('[ECommerce] Error: ' + e.message);
    }
  }

  registration(user: UserInfo) {
    window.dataLayer.push({
      event: 'registration',
      user,
    });
  }

  authorization(email: string, phone?: string) {
    window.dataLayer.push({
      event: 'authorization',
      user: {
        phone,
      },
    });
  }

  impressionsList(items: ECommerceProduct[], listName) {
    try {
      this.info(items, data => {
        const products = [];
        let listType = '';
        let listPrefix = '';

        if (listName === 'Карточка модели') {
          listType = 'Model card list';
          listPrefix = 'mcl_';
        } else if (listName === 'Результаты фильтра') {
          listType = 'Product list';
          listPrefix = 'pl_';
        } else if (listName === 'Список моделей') {
          listType = 'Model list';
          listPrefix = 'ml_';
        }

        data?.forEach((value, index) => {
          const product = this.productInfo(value);
          if (listName === 'Список моделей') {
            product.name = 'm' + product.name.substr(1);
            product.id = 'm_' + product.id;
          }
          product.list = listPrefix + window.location.pathname + window.location.search;
          product.position = items[index].position;
          products.push(product);
        });

        window.dataLayer.push({
          event: 'impressions',
          eventLabel: listType,
          ecommerce: {
            impressions: products,
          },
        });
      });
    } catch (e) {
      console.error('[ECommerce] Error: ' + e.message);
    }
  }

  viewCart(items: ECommerceProduct[], total: number) {
    const qtys = [];

    for (let i = 0; i < items.length; i++) {
      const citem = items[i];
      qtys[citem.id] = citem.quantity;
    }

    try {
      this.info(items, data => {
        const products = [];

        data?.forEach(item => {
          const productId = this.convertAtomToStringOrNull(item.id);

          let quantity = 0;
          if (!this.isNil(qtys[productId])) {
            quantity = parseInt(qtys[productId]);
          }

          const product = this.productInfo(item);
          product.quantity = this.convertAtomToStringOrNull(quantity);
          products.push(product);
        });

        window.dataLayer.push({
          event: 'view_cart',
          ecommerce: {
            currency: 'RUB',
            value: total,
            products,
          },
        });
      });
    } catch (e) {
      console.error('[ECommerce] Error: ' + e.message);
    }
  }

  checkout(items: ECommerceProduct[], step) {
    const qtys = [];

    for (let i = 0; i < items.length; i++) {
      const citem = items[i];
      qtys[citem.id] = citem.quantity;
    }

    try {
      this.info(items, data => {
        const products = [];
        let stepLabel = '';
        let stepNumber = '';
        const option = '';

        switch (step) {
          case 'delivery':
            stepLabel = 'Step Delivery';
            stepNumber = '1';
            break;
          case 'payment':
            stepLabel = 'Step Payment';
            stepNumber = '2';
            break;
          case 'contacts':
            stepLabel = 'Step Customer';
            stepNumber = '3';
            break;
        }

        data?.forEach(item => {
          const productId = this.convertAtomToStringOrNull(item.id);

          let quantity = 0;
          if (!this.isNil(qtys[productId])) {
            quantity = parseInt(qtys[productId]);
          }

          const product = this.productInfo(item);
          product.quantity = this.convertAtomToStringOrNull(quantity);
          products.push(product);
        });

        if (step === 'confirm') {
          window.dataLayer.push({
            event: 'checkoutOption',
            eventLabel: stepLabel,
            ecommerce: {
              checkout_option: {
                actionField: {
                  step: stepNumber,
                  option,
                },
              },
            },
          });
        } else {
          window.dataLayer.push({
            event: 'checkout',
            eventLabel: stepLabel,
            ecommerce: {
              checkout: {
                products,
                actionField: {
                  step: stepNumber,
                },
              },
            },
          });
        }
      });
    } catch (e) {
      console.error('[ECommerce] Error: ' + e.message);
    }
  }

  //  Такие параметры мы собирали со старого koleso.ru:
  //  'event'              : 'tyrefitting', // для google analytics
  //  'goal_id'            : '41414386', // id цели яндекс метрики - константа
  //  'tyrefitting_id'     : '154645', //id записи на шиномонтаж - динамический
  //  'client_status'      : 'physical' или 'corporate', //физическое или юрлицо
  //  'hranenie_status'    : 'yes' или 'no', //хранятся шины у нас или нет
  //  'tyrefitting_address': 'адрес выбраной точки' // город, улица
  //
  //  Такие параметры мы собирали со старого koleso.ru:
  //  'event': 'serviceRecord', // успешная запись на услугу для google analytics
  //  'goal_id': '41414386', // id цели яндекс метрики
  //  'service_name': 'Название_услуги' // название услуги, на которую записан человек
  serviceRecord(params) {
    params = {
      ...params,
      event: 'serviceRecord',
      goal_id: '41414386',
      actionField: {
        goal_id: '41414386',
      },
    };

    window.dataLayer.push(params);
  }

  removeFromWishList(item: ECommerceProduct, label) {
    try {
      this.info([item], data => {
        const products = [];
        let total = 0;

        data.forEach(item => {
          const product = this.productInfo(item);
          products.push(product);

          let priceOne = 0;
          if (!this.isNil(item.product.price)) {
            priceOne = parseFloat(item.product.price);
          }

          total += priceOne;
        });

        window.dataLayer.push({
          event: 'remove_from_wishlist',
          eventLabel: label,
          ecommerce: {
            currency: 'RUB',
            value: total,
            items: products,
          },
        });
      });
    } catch (e) {
      console.error('[ECommerce] Error: ' + e.message);
    }
  }
}

const serviceEcommerceInstance = new ServiceEcommerce();

export default serviceEcommerceInstance;
